// ClientFormFisaServiceDetails.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { pdf } from "@react-pdf/renderer";
import { QRCodeCanvas } from "qrcode.react";

import {
  selectStyles,
  getNowLocalDate,
  getNowLocalTimeHHMM,
  buildSelectOptionsFromLocations,
  buildClientSignatureLink,
  resolvePictureUrl,
  resetTime,
  formatRoDateTime,
} from "../../util/common";

import ClientFormFisaServicePdfTemplate from "../../components/PdfTemplates/ClientFormFisaServicePdfTemplate";

import * as StorageService from "../../services/StorageServices";
import { LOCALSTORAGE_ITEMS, DURATA_UNITS } from "../../util/Constants";
import defaultLogo from "../../assets/img/logo-mc-business.jpg";

import {
  createClientFormHandler,
  editClientFormByIdHandler,
  getClientFormByIdHandler,
  getUserLocationsHandler,
  getClockInOutHandler, 
} from "../../handlers/clientFormsHandlers/HandlerClientForms";

import TimeInput24h from "../../components/TimeInput24h.js";
import Spinner from "../../components/Spinner";
import { makeSelectStylesGreenChecks, greenCheckComponents } from "../../util/selectGreenChecks";

import {
  IconUser,
  IconCalendar,
  IconClock,
  IconClipboardList,
  IconPackage,
  IconFileText,
  IconWrench,
  IconBanknote,
  IconTriangleAlert,
} from "../../components/Icons";

import IconBadge from "./components/IconBadge";
import FieldLabel from "./components/FieldLabel";
import StatusBadge from "../../components/StatusBadge";

import useWaitForClientSignature from "./hooks/useWaitForClientSignature.js";
import useDefaultLocationFromClockIn from "./hooks/useDefaultLocationFromClockIn";
function ClientFormFisaServiceDetails(props) {
  console.log("BonDeComandaDetails", props);

  const STATUS = {
    NEW: "NEW",
    PENDING: "PENDING",
    SIGNED: "SIGNED",
    CONFIRMED: "CONFIRMED",
  };

  const FORM_ID_PREFIX = "FISA_SERVICE";

  const companyInfo = StorageService.getItem(LOCALSTORAGE_ITEMS.COMPANY_INFO)
    ? JSON.parse(StorageService.getItem(LOCALSTORAGE_ITEMS.COMPANY_INFO))
    : null;

  const firstName = StorageService.getItem(LOCALSTORAGE_ITEMS.FIRST_NAME) || "Unknown";
  const lastName = StorageService.getItem(LOCALSTORAGE_ITEMS.LAST_NAME) || "Unknown";

  const employeeId = StorageService.getItem(LOCALSTORAGE_ITEMS.USER_ID) || null;
  const companyId = companyInfo?.id ? String(companyInfo.id) : null;

  const FIELD_KEYS = {
    companyName: "companyName",
    employeeName: "employeeName",
    inputDate: "inputDate",
    ora: "ora",
    numeClient: "numeClient",
    clientPhone: "clientPhone",
    productDescription: "productDescription",
    defectChoice: "defectChoice",
    defecteText: "defecteText",
    operatiuneValues: "operatiuneValues",
    operatiuneAltulText: "operatiuneAltulText",
    observatii: "observatii",
    estimareDurataValue: "estimareDurataValue",
    estimareDurataUnit: "estimareDurataUnit",
    pretTotalLei: "pretTotalLei",
    riscuri: "riscuri",
    acknowledged: "acknowledged",
    subsemnatulName: "subsemnatulName",
    angajatulName: "angajatulName",
    dataSubsemnatul: "dataSubsemnatul",
    dataAngajatul: "dataAngajatul",

    // backwards compat only
    semnaturaSubsemnatul: "semnaturaSubsemnatul",
    semnaturaAngajatul: "semnaturaAngajatul",

    locationId: "locationId",
  };

  const OPERATIUNI = useMemo(
    () => [
      { value: "copiere_cheie_cu_cip", label: "Copiere cheie cu cip" },
      {
        value: "copiere_cheie_cu_loc_pentru_cip_si_mutare_transponder",
        label: "Copiere cheie cu loc pentru cip si mutare transponder",
      },
      { value: "schimbare_carcase_auto", label: "Schimbare carcase auto*" },
      { value: "prelucrare_chei_deteriorate", label: "Prelucrare chei deteriorate**" },
      { value: "schimbare_baterie", label: "Schimbare baterie" },
      { value: "mutare_transponder", label: "Mutare Transponder (CIP)***" },
      { value: "prelucrare_cheia_clientului", label: "Prelucrare cheia clientului****" },
      { value: "lipit_butoane_telecomenzi", label: "Lipit butoane telecomenzi" },
      { value: "alta", label: "Alta" },
    ],
    []
  );

  const getField = (fieldsObj, key, fallback = null) => {
    if (!fieldsObj) return fallback;
    const v = fieldsObj[key];
    return typeof v === "undefined" || v === null ? fallback : v;
  };

  const defaultFormFields = {
    companyName: "",
    logoUrl: defaultLogo,

    userLocation: null,
    employeeName: `${firstName} ${lastName}`.trim(),
    inputDate: getNowLocalDate(),
    ora: getNowLocalTimeHHMM(),
    numeClient: "",
    clientPhone: "",
    productDescription: "",

    defectChoice: "none",
    defecteText: "",

    operatiune: [],
    operatiuneAltulText: "",

    observatii: "",

    estimareDurataValue: "",
    estimareDurataUnit: DURATA_UNITS[0],
    pretTotalLei: "",

    riscuri: "",

    acknowledged: true,
    subsemnatulName: "",
    angajatulName: `${firstName} ${lastName}`.trim(),
    dataSubsemnatul: getNowLocalDate(),
    dataAngajatul: getNowLocalDate(),
    semnaturaSubsemnatul: null,
    semnaturaAngajatul: null,

    // receipt
    receipt: "",
    receiptInput: "",
    receiptDate: null,
    savingReceipt: false,
    receiptMsg: null,
    receiptError: null,

    status: STATUS.NEW,
    createdFormId: null,
    publicLink: null,

    loading: true,
    errorMsg: null,
  };

  const [state, setState] = useState({ ...defaultFormFields });
  const fileRef = useRef(null);

  const [userLocations, setUserLocations] = useState([]);

  const userLocationOptions = useMemo(() => {
    return (userLocations || []).map((l) => ({
      value: String(l.id),
      label: l.name || `Locație ${l.id}`,
      email: l.email || "",
      phone: l.phone || "",
      address: l.address || "",
      employer: l.employer || "",
      raw: l,
    }));
  }, [userLocations]);

  const selectStylesGreenChecks = useMemo(() => makeSelectStylesGreenChecks(selectStyles), []);

  const hasAltaOperatiune = (state.operatiune || []).some((o) => o?.value === "alta");

  const effectiveId = props.documentId || state.createdFormId;
  const isNew = !effectiveId;

  const formStatus = String(state.status || "").toUpperCase();
  console.log("formStatus: "+formStatus);
  const isSigned = (formStatus === STATUS.SIGNED) || (formStatus === STATUS.CONFIRMED);
  const isReadonly = isSigned;

  const hasSingleUserLocation = userLocationOptions.length === 1;
  const canGenerate = !isReadonly && state.status === STATUS.NEW;

  const resolveFormIdFromCompanyInfo = () => {
    const list = companyInfo?.availableClientForms ?? [];
    return list.find((id) => id?.startsWith?.(FORM_ID_PREFIX)) ?? null;
  };

  useWaitForClientSignature({
    formId: state.createdFormId,
    status: state.status,
    isSigned,
    getClientFormById: getClientFormByIdHandler,
    onSigned: async () => {
      await getPageContentWithLocations(userLocations);
    },
  });


  const { resolveDefaultLocationFromClockIn } = useDefaultLocationFromClockIn({
  companyId,
  employeeId,
  getClockInOutHandler,
});

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setState((s) => ({ ...s, loading: true, errorMsg: null }));

      const locResp = await getUserLocationsHandler();
      const locItems = locResp?.data?.items || [];
      if (cancelled) return;

      const safeLocs = Array.isArray(locItems) ? locItems : [];
      setUserLocations(safeLocs);

      await getPageContentWithLocations(safeLocs);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [props.documentId]); 

  const mapClientFormToState = (cf, locationsFromApi) => {
    const optionsNow = buildSelectOptionsFromLocations(locationsFromApi || userLocations);

    const cfId = cf?.id || null;
    const cfStatus = String(cf?.status || STATUS.PENDING).toUpperCase();
    const fields = cf?.fields || {};

    const link = cfId ? buildClientSignatureLink(cfId, "FISA_SERVICE") : null;

    const savedLocationId = String(cf?.location?.id || getField(fields, FIELD_KEYS.locationId, "") || "");
    const savedLocationOpt = optionsNow.find((o) => o.value === String(savedLocationId)) || optionsNow[0] || null;

    const opValues = getField(fields, FIELD_KEYS.operatiuneValues, []);
    const opSel = (Array.isArray(opValues) ? opValues : [])
      .map((v) => OPERATIUNI.find((o) => o.value === v))
      .filter(Boolean);

    const unitVal = getField(fields, FIELD_KEYS.estimareDurataUnit, DURATA_UNITS[0].value);
    const unitSel = DURATA_UNITS.find((u) => u.value === unitVal) || DURATA_UNITS[0];

    const picture = resolvePictureUrl(companyInfo?.pictureUrl || companyInfo?.picture);
    const logoUrl = picture || defaultLogo;

    const receipt = String(cf?.receipt || "").trim();
    const receiptDate = cf?.receiptDate || null;

    return {
      status: cfStatus,
      createdFormId: cfId,
      publicLink: cfStatus === STATUS.PENDING ? link : null,

      userLocation: savedLocationOpt,

      companyName: String(getField(fields, FIELD_KEYS.companyName, companyInfo?.name || "") || ""),
      logoUrl,

      employeeName: String(getField(fields, FIELD_KEYS.employeeName, "") || ""),
      inputDate: String(getField(fields, FIELD_KEYS.inputDate, "") || ""),
      ora: String(getField(fields, FIELD_KEYS.ora, "") || ""),

      numeClient: String(getField(fields, FIELD_KEYS.numeClient, "") || ""),
      clientPhone: String(getField(fields, FIELD_KEYS.clientPhone, "") || ""),
      productDescription: String(getField(fields, FIELD_KEYS.productDescription, "") || ""),

      defectChoice: String(getField(fields, FIELD_KEYS.defectChoice, "none") || "none"),
      defecteText: String(getField(fields, FIELD_KEYS.defecteText, "") || ""),

      operatiune: opSel,
      operatiuneAltulText: String(getField(fields, FIELD_KEYS.operatiuneAltulText, "") || ""),

      observatii: String(getField(fields, FIELD_KEYS.observatii, "") || ""),

      estimareDurataValue: String(getField(fields, FIELD_KEYS.estimareDurataValue, "") || ""),
      estimareDurataUnit: unitSel,
      pretTotalLei: String(getField(fields, FIELD_KEYS.pretTotalLei, "") || ""),

      riscuri: String(getField(fields, FIELD_KEYS.riscuri, "") || ""),

      acknowledged: Boolean(getField(fields, FIELD_KEYS.acknowledged, true)),

      subsemnatulName: String(getField(fields, FIELD_KEYS.subsemnatulName, "") || ""),
      angajatulName: String(getField(fields, FIELD_KEYS.angajatulName, "") || ""),
      dataSubsemnatul: String(getField(fields, FIELD_KEYS.dataSubsemnatul, "") || ""),
      dataAngajatul: String(getField(fields, FIELD_KEYS.dataAngajatul, "") || ""),

    
      semnaturaSubsemnatul: cf?.clientSignature || getField(fields, FIELD_KEYS.semnaturaSubsemnatul, null) || null,
      semnaturaAngajatul: cf?.employeeSignature || getField(fields, FIELD_KEYS.semnaturaAngajatul, null) || null,

      // receipt
      receipt,
      receiptInput: receipt,
      receiptDate,
      savingReceipt: false,
      receiptMsg: null,
      receiptError: null,
    };
  };

  const getPageContentWithLocations = async (locs) => {
    console.log("getPageContentWithLocations", locs)
    console.log("isNew", isNew)
    
    const optionsNow = buildSelectOptionsFromLocations(locs);

    // NEW
    if (isNew) {
      let initialLoc = (optionsNow || [])[0] || null;

      try {
        console.log("getPageContentWithLocations call resolveDefaultLocationFromClockIn with ", optionsNow)
        const clockedOpt = await resolveDefaultLocationFromClockIn(optionsNow);
        if (clockedOpt) initialLoc = clockedOpt;
      } catch(err) {
        console.error("resolveDefaultLocationFromClockIn", err)
        // ignore & fallback
      }

      const picture = resolvePictureUrl(companyInfo?.pictureUrl || companyInfo?.picture);
      const logoUrl = picture || defaultLogo;

      setState(() => ({
        ...defaultFormFields,
        userLocation: initialLoc,

        companyName: companyInfo?.name || "",
        logoUrl,

        employeeName: `${firstName} ${lastName}`.trim(),
        inputDate: getNowLocalDate(),
        ora: getNowLocalTimeHHMM(),

        angajatulName: `${firstName} ${lastName}`.trim(),
        dataSubsemnatul: getNowLocalDate(),
        dataAngajatul: getNowLocalDate(),

        loading: false,
        errorMsg: null,
      }));
      return;
    }

    // EDIT / PREVIEW
    const idToLoad = effectiveId;
    const resp = await getClientFormByIdHandler(idToLoad);
    const cf = resp?.data?.clientForm;

    setState(() => ({
      ...defaultFormFields,
      ...mapClientFormToState(cf, locs),
      loading: false,
      errorMsg: null,
    }));
  };

  /**
   * IMPORTANT:
   * - signatures are NOT included in `fields` anymore
   * - signatures are sent only as top-level
   */
  const buildClientFormPayload = () => {
    const fields = {};

    fields[FIELD_KEYS.companyName] = state.companyName || "";
    fields[FIELD_KEYS.locationId] = state.userLocation?.value || null;

    fields[FIELD_KEYS.employeeName] = state.employeeName || "";
    fields[FIELD_KEYS.inputDate] = state.inputDate || "";
    fields[FIELD_KEYS.ora] = state.ora || "";
    fields[FIELD_KEYS.numeClient] = state.numeClient || "";
    fields[FIELD_KEYS.clientPhone] = state.clientPhone || "";
    fields[FIELD_KEYS.productDescription] = state.productDescription || "";

    fields[FIELD_KEYS.defectChoice] = state.defectChoice || "none";
    fields[FIELD_KEYS.defecteText] = state.defectChoice === "has" ? (state.defecteText || "").trim() : "";

    fields[FIELD_KEYS.operatiuneValues] = (state.operatiune || []).map((o) => o.value);
    fields[FIELD_KEYS.operatiuneAltulText] = hasAltaOperatiune ? (state.operatiuneAltulText || "").trim() : "";

    fields[FIELD_KEYS.observatii] = (state.observatii || "").trim();

    fields[FIELD_KEYS.estimareDurataValue] = state.estimareDurataValue || "";
    fields[FIELD_KEYS.estimareDurataUnit] = state.estimareDurataUnit?.value ?? "minute";
    fields[FIELD_KEYS.pretTotalLei] = state.pretTotalLei || "";

    fields[FIELD_KEYS.riscuri] = (state.riscuri || "").trim();

    fields[FIELD_KEYS.acknowledged] = Boolean(state.acknowledged);
    fields[FIELD_KEYS.subsemnatulName] = state.subsemnatulName || "";
    fields[FIELD_KEYS.angajatulName] = state.angajatulName || "";
    fields[FIELD_KEYS.dataSubsemnatul] = state.dataSubsemnatul || "";
    fields[FIELD_KEYS.dataAngajatul] = state.dataAngajatul || "";

    const locationId = state.userLocation?.value || null;
    const formId = resolveFormIdFromCompanyInfo();

    return {
      id: isNew ? undefined : props.documentId,
      employeeId,
      locationId,
      formId,
      companyId,
      fields,
      clientName: state.numeClient || "",
      clientPhone: state.clientPhone || "",

      // signatures only top-level
      clientSignature: state.semnaturaSubsemnatul || null,
      employeeSignature: state.semnaturaAngajatul || null,
    };
  };

  const validate = () => {
    const errors = [];
    const isEmpty = (v) => String(v ?? "").trim().length === 0;

    if (isEmpty(state.employeeName)) errors.push("Angajat");
    if (isEmpty(state.inputDate)) errors.push("Data");
    if (isEmpty(state.ora)) errors.push("Ora");

    // if (isEmpty(state.numeClient)) errors.push("Cumpărător");
    // if (isEmpty(state.clientPhone)) errors.push("Telefon cumpărător");

    if (isEmpty(state.productDescription)) errors.push("Descriere produs primit");

    if (!Array.isArray(state.operatiune) || state.operatiune.length === 0) errors.push("Operațiune");
    if (hasAltaOperatiune && isEmpty(state.operatiuneAltulText)) errors.push("Alta (Operațiune)");

    if (state.defectChoice === "has" && isEmpty(state.defecteText)) errors.push("Defecte produs");

    if (isEmpty(state.estimareDurataValue)) errors.push("Durată estimată");
    if (!state.estimareDurataUnit?.value) errors.push("Unitate durată");
    if (isEmpty(state.pretTotalLei)) errors.push("Preț total (include TVA)");

    const formId = resolveFormIdFromCompanyInfo();
    const locationId = state.userLocation?.value || null;

    if (!employeeId || !companyId || !formId || !locationId) {
      errors.push("Config (employeeId / companyId / formId / locationId)");
    }

    if (errors.length > 0) {
      setState((s) => ({
        ...s,
        errorMsg: `Există câmpuri incomplete. Lipsesc: ${errors.join(", ")}.`,
      }));
      return false;
    }

    setState((s) => ({ ...s, errorMsg: null }));
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = buildClientFormPayload();
    const resp = isNew ? await createClientFormHandler(payload) : await editClientFormByIdHandler(effectiveId, payload);

    if (resp?.data?.error != null) {
      setState((s) => ({ ...s, errorMsg: resp.data?.message || "Error" }));
      return;
    }

    if (isNew) {
      const cf = resp?.data?.clientForm || resp?.data;
      setState((s) => ({
        ...s,
        ...defaultFormFields,
        ...mapClientFormToState(cf, userLocations),
        loading: false,
        errorMsg: null,
      }));
      return;
    }

    props.closeRightPanel(true);
  };

  const CheckboxRow = ({ checked, onClick, label }) => (
    <div
      className="row"
      style={{
        alignItems: "center",
        gap: 10,
        cursor: isReadonly ? "not-allowed" : "pointer",
      }}
      onClick={isReadonly ? undefined : onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (isReadonly ? null : e.key === "Enter" || e.key === " " ? onClick() : null)}
    >
      <div className="checkbox" style={{ cursor: isReadonly ? "not-allowed" : "pointer" }}>
        <input type="checkbox" checked={checked} onChange={isReadonly ? undefined : onClick} readOnly={isReadonly} />
        <div className="checksquare"></div>
      </div>
      <p style={{ margin: 0 }}>{label}</p>
    </div>
  );

  if (state.loading) return <Spinner />;

  const handlePrintPdf = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    try {
      const docData = {
        ...state,
        userLocation: state.userLocation,
        clientSignature: state.semnaturaSubsemnatul,
        employeeSignature: state.semnaturaAngajatul,
        receiptDate: formatRoDateTime(state.receiptDate, { withTime: true }),
      };

      const blob = await pdf(<ClientFormFisaServicePdfTemplate data={docData} />).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error("handlePrintPdf", err);
      setState((s) => ({ ...s, errorMsg: "Nu am putut genera PDF-ul pentru deschidere." }));
    }
  };

  /**
   * Receipt update
   */
  const handleSaveReceipt = async () => {
    if (!effectiveId) return;

    const receipt = String(state.receiptInput || "").trim();
    if (!receipt) {
      setState((s) => ({
        ...s,
        receiptError: "Introdu numărul bonului.",
        receiptMsg: null,
        errorMsg: null,
      }));
      return;
    }

    setState((s) => ({
      ...s,
      savingReceipt: true,
      receiptMsg: null,
      receiptError: null,
      errorMsg: null,
    }));

    try {
      const payloadBase = buildClientFormPayload();

      const fields = { ...(payloadBase.fields || {}) };
      delete fields[FIELD_KEYS.semnaturaSubsemnatul];
      delete fields[FIELD_KEYS.semnaturaAngajatul];

      const payload = {
        id: effectiveId,
        locationId: payloadBase.locationId,
        fields,
        clientName: payloadBase.clientName,
        clientPhone: payloadBase.clientPhone,
        clientSignature: state.semnaturaSubsemnatul ?? undefined,
        employeeSignature: state.semnaturaAngajatul ?? undefined,
        receipt,
      };

      const resp = await editClientFormByIdHandler(effectiveId, payload);

      if (resp?.data?.error != null) {
        setState((s) => ({
          ...s,
          receiptMsg: null,
          receiptError: resp.data?.message || "Nu am putut salva numărul bonului.",
          savingReceipt: false,
        }));
        return;
      }

      const updated = resp?.data?.clientForm ? resp.data.clientForm : resp?.data;

      setState((s) => ({
        ...s,
        receipt: String(updated?.receipt ?? receipt),
        receiptInput: String(updated?.receipt ?? receipt),
        receiptDate: updated?.receiptDate || s.receiptDate,
        receiptMsg: "Numărul bonului a fost salvat.",
        receiptError: null,
        savingReceipt: false,
      }));
    } catch (err) {
      console.error("handleSaveReceipt", err);
      setState((s) => ({
        ...s,
        savingReceipt: false,
        receiptMsg: null,
        receiptError: "Nu am putut salva numărul bonului.",
      }));
    }
  };

  const showReceiptUpdate = isSigned;

  return (
    <section className="form-container">
      <div className="details-form bon-details">
        {/* HEADER */}
        <div className="form-group service-form-group no-bg ">
          <div className="row fisa-header-row">
            <div className="form-group service-form-group centered no-bg mb-0">
              <div className="row">
                <div className="logo-upload" role="button" tabIndex={0}>
                  <img className="logo-img" src={state.logoUrl || defaultLogo} alt="logo" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-title-and-subtitle centered">
          <div className="row title-row centered">
            <h1 className="title">FIȘĂ SERVICE</h1>
          </div>

          {state.status !== STATUS.NEW ? <StatusBadge status={state.status} /> : null}

          {isReadonly ? (
            <button type="button" onClick={handlePrintPdf} className="btn btn-success" style={{ marginTop: 10 }}>
              Printează
            </button>
          ) : null}
        </div>

        {/* 1) DATE BON COMANDA */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconClipboardList />
            </IconBadge>
            <div className="row form-group-title">Date bon</div>
          </div>

          <div className="row" style={{ flexDirection: "column", gap: 12, marginTop: 10 }}>
            <div className="row" style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span>Subsemnatul</span>
              <input className="input" style={{ width: 260 }} value={state.employeeName} readOnly />
              <span>, în calitate de angajat al </span>
              <input className="input" style={{ width: 260 }} value={state.userLocation?.employer || state.companyName} readOnly />
              <span>,</span>

              <span> cu punctul de lucru în incinta</span>

              <div className="field-row" style={{ margin: 0, width: "min(350px, 100%)" }}>
                {hasSingleUserLocation ? (
                  <div className="input" style={{ display: "flex", alignItems: "center" }}>
                    {userLocationOptions[0]?.label || "-"}
                  </div>
                ) : (
                  <Select
                    className="select"
                    options={userLocationOptions}
                    value={state.userLocation}
                    onChange={(opt) => {
                      if (isReadonly) return;
                      setState((s) => ({ ...s, userLocation: opt, errorMsg: null }));
                    }}
                    isDisabled={isReadonly}
                    styles={selectStylesGreenChecks}
                    components={{
                      ...greenCheckComponents,
                      IndicatorSeparator: () => null,
                    }}
                  />
                )}
              </div>

              <span>, am primit azi</span>
              <input
                className="input"
                type="date"
                style={{ width: 150 }}
                value={state.inputDate}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, inputDate: e.target.value, errorMsg: null }))}
              />
              <span>ora</span>

              {isReadonly ? (
                <input className="input w-100" value={state.ora} readOnly />
              ) : (
                <TimeInput24h
                  className="input w-100"
                  value={state.ora}
                  onChange={(val) => setState((s) => ({ ...s, ora: val, errorMsg: null }))}
                  placeholder="16:00"
                />
              )}

              <span>,</span>
            </div>

            <div className="row" style={{ flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              <span>De la subsemnatul</span>
              <input
                className="input"
                style={{ width: 320 }}
                value={state.numeClient}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, numeClient: e.target.value, errorMsg: null }))}
              />
              <span>în calitate de cumpărător, nr de telefon:</span>
              <input
                className="input"
                placeholder="Nr. de tel. opțional"
                style={{ width: 220 }}
                value={state.clientPhone}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, clientPhone: e.target.value, errorMsg: null }))}
              />
            </div>

            <div className="row" style={{ flexDirection: "column", width: "100%", gap: "8px", textAlign: "left" }}>
              <div className="text-left">Următorul produs:</div>
              <textarea
                className="input"
                rows={4}
                value={state.productDescription}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, productDescription: e.target.value, errorMsg: null }))}
              />
            </div>
          </div>
        </div>

        {/* 2) PRODUSUL */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconPackage />
            </IconBadge>
            <div className="row form-group-title">Produsul</div>
          </div>

          <div className="input" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            <CheckboxRow
              checked={state.defectChoice === "none"}
              onClick={() => setState((s) => ({ ...s, defectChoice: "none", errorMsg: null }))}
              label="Nu prezintă defecte"
            />
            <CheckboxRow
              checked={state.defectChoice === "has"}
              onClick={() => setState((s) => ({ ...s, defectChoice: "has", errorMsg: null }))}
              label="Prezinta următoarele defecte :"
            />
          </div>

          {state.defectChoice === "has" ? (
            <div className="field-row" style={{ marginTop: 10 }}>
              <FieldLabel icon={IconFileText}>Defecte</FieldLabel>
              <textarea
                className="input"
                rows={4}
                value={state.defecteText}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, defecteText: e.target.value, errorMsg: null }))}
              />
            </div>
          ) : null}
        </div>

        {/* 3) OPERAȚIUNE */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconWrench />
            </IconBadge>
            <div className="row form-group-title">Acestui produs i se va efectua următoarea operațiune:</div>
          </div>

          <div className="row" style={{ marginTop: 10 }}>
            <Select
              className="select"
              isMulti
              options={OPERATIUNI}
              value={state.operatiune}
              isDisabled={isReadonly}
              onChange={(opt) => setState((s) => ({ ...s, operatiune: opt || [], errorMsg: null }))}
              styles={selectStylesGreenChecks}
              components={{ ...greenCheckComponents, IndicatorSeparator: () => null }}
            />
          </div>

          {hasAltaOperatiune ? (
            <div className="field-row" style={{ marginTop: 10 }}>
              <FieldLabel icon={IconWrench}>Alta</FieldLabel>
              <input
                className="input"
                value={state.operatiuneAltulText}
                readOnly={isReadonly}
                onChange={(e) => setState((s) => ({ ...s, operatiuneAltulText: e.target.value, errorMsg: null }))}
              />
            </div>
          ) : null}
        </div>

        {/* 4) OBSERVATII */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconFileText />
            </IconBadge>
            <div className="row form-group-title">OBSERVAȚII</div>
          </div>

          <textarea
            className="input"
            rows={6}
            value={state.observatii}
            readOnly={isReadonly}
            onChange={(e) => setState((s) => ({ ...s, observatii: e.target.value, errorMsg: null }))}
            style={{ marginTop: 10 }}
          />

          <div className="row" style={{ marginTop: 10, fontSize: 12, opacity: 0.9 }}>
            *transfer placuță electronică și cip; **chei rupte/chei uzate; ***transferul cip-ului dintr-o cheie în alta;
            ****gravare/tăiere lamelă;
          </div>
        </div>

        {/* 5) ESTIMARE */}
        <div className="form-group service-form-group">
          <div className="form-group-title-row">
            <IconBadge>
              <IconBanknote />
            </IconBadge>
            <div className="row form-group-title">Estimare și preț (include TVA)</div>
          </div>

          <div className="row" style={{ flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 10 }}>
            <span>Întreaga operațiune va dura</span>
            <input
              className="input"
              style={{ width: 120 }}
              value={state.estimareDurataValue}
              readOnly={isReadonly}
              onChange={(e) => setState((s) => ({ ...s, estimareDurataValue: e.target.value, errorMsg: null }))}
            />
            <Select
              className="select no-width"
              options={DURATA_UNITS}
              value={state.estimareDurataUnit}
              isDisabled={isReadonly}
              onChange={(opt) => setState((s) => ({ ...s, estimareDurataUnit: opt, errorMsg: null }))}
              styles={selectStylesGreenChecks}
              components={greenCheckComponents}
            />
            <span>, iar prețul inluzând TVA va fi de</span>
            <input
              className="input"
              style={{ width: 140 }}
              value={state.pretTotalLei}
              readOnly={isReadonly}
              onChange={(e) => setState((s) => ({ ...s, pretTotalLei: e.target.value, errorMsg: null }))}
            />
            <span>lei.</span>
          </div>
        </div>

        {/* 6) RISCURI */}
        <div className="form-group service-form-group warning">
          <div className="form-group-title-row">
            <IconBadge>
              <IconTriangleAlert />
            </IconBadge>
            <div className="row form-group-title">Riscuri previzibile în urma acestei operațiuni:</div>
          </div>

          <textarea
            className="input"
            rows={4}
            value={state.riscuri}
            readOnly={isReadonly}
            onChange={(e) => setState((s) => ({ ...s, riscuri: e.target.value, errorMsg: null }))}
            style={{ marginTop: 10 }}
          />
        </div>

        {/* Actualizare BON - show only after SIGNED */}
        {showReceiptUpdate ? (
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <IconBadge>
                <IconClipboardList />
              </IconBadge>
              <div className="row form-group-title">Actualizare Bon</div>
            </div>

            <div className="field-row two" style={{ alignItems: "center" }}>
              <FieldLabel className="w-150" icon={IconFileText}>
                Număr bon
              </FieldLabel>

              <input
                className="input"
                value={state.receiptInput}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    receiptInput: e.target.value,
                    receiptMsg: null,
                    receiptError: null,
                    errorMsg: null,
                  }))
                }
                placeholder="Ex: 12345"
                autoComplete="off"
              />

              <button
                type="button"
                className="btn btn-success"
                onClick={handleSaveReceipt}
                disabled={state.savingReceipt}
                style={{ minWidth: 170 }}
              >
                {state.savingReceipt ? "Se salvează..." : "Salvează număr bon"}
              </button>
            </div>

            {state.receiptMsg ? (
              <div className="error success" style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                {state.receiptMsg}
              </div>
            ) : null}

            {state.receiptError ? (
              <div className="error" style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                {state.receiptError}
              </div>
            ) : null}

            {state.receiptDate ? (
              <div className="field-row" style={{ marginTop: 8 }}>
                <FieldLabel className="w-150" icon={IconCalendar}>
                  Data bon
                </FieldLabel>
                <input
                  className="input"
                  value={formatRoDateTime(state.receiptDate, { withTime: true })}
                  readOnly
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {/* 7) ACORD + SEMNĂTURI (only after SIGNED) */}
        {isSigned ? (
          <div className="form-group service-form-group success">
            <div className="form-group-title-row">
              <IconBadge>
                <IconFileText />
              </IconBadge>
              <div className="row form-group-title">Acord și semnături</div>
            </div>

            <div className={"agreed input"} style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 600 }}>
                <div className="row" style={{ cursor: "not-allowed" }} role="button" tabIndex={0}>
                  <div className="checkbox" style={{ cursor: "not-allowed" }}>
                    <input type="checkbox" checked={Boolean(state.acknowledged)} readOnly />
                    <div className="checksquare"></div>
                  </div>
                  <p style={{ margin: 0 }}>Am luat la cunoștință riscurile acestei operațiuni,</p>
                </div>
              </div>
            </div>

            <div className="field-row two" style={{ marginTop: 10 }}>
              <FieldLabel icon={IconUser}>Subsemnatul:</FieldLabel>
              <input className="input" value={state.numeClient || ""} readOnly />

              <FieldLabel icon={IconUser}>Angajatul:</FieldLabel>
              <input className="input" value={state.employeeName || ""} readOnly />
            </div>

            <div className="field-row two">
              <FieldLabel icon={IconCalendar}>Data:</FieldLabel>
              <input className="input" type="date" value={state.dataSubsemnatul || ""} readOnly />

              <FieldLabel icon={IconCalendar}>Data:</FieldLabel>
              <input className="input" type="date" value={state.dataAngajatul || ""} readOnly />
            </div>

            <div className="field-row two" style={{ alignItems: "flex-start" }}>
              <FieldLabel icon={IconFileText}>Semnătura client:</FieldLabel>
              <div className="input dotted signature-input" style={{ padding: 10, minHeight: 120, overflow: "hidden" }}>
                {state.semnaturaSubsemnatul ? (
                  <img
                    src={state.semnaturaSubsemnatul}
                    alt="Semnătura client"
                    style={{ maxWidth: "100%", maxHeight: 160, display: "block" }}
                  />
                ) : (
                  <div style={{ opacity: 0.6 }}>—</div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* PENDING: QR */}
        {state.status === STATUS.PENDING && state.createdFormId && state.publicLink ? (
          <div className="form-group service-form-group">
            <div className="form-group-title-row">
              <div className="row form-group-title">Semnare client (QR)</div>
            </div>

            <div className="row" style={{ alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div>
                <QRCodeCanvas value={state.publicLink} size={180} includeMargin />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontWeight: 600 }}>Scanează cu telefonul pentru a semna fișa service</div>

                <a className="link ml-0" href={state.publicLink} target="_blank" rel="noreferrer">
                  Deschide declarația în tab nou
                </a>

                <div style={{ fontSize: 12, opacity: 0.75 }}>ID: {state.createdFormId}</div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="row buttons-container">
          <button onClick={() => props.closeRightPanel(true)} className="btn btn-default">
            {isReadonly || state.createdFormId ? "Închide" : "Anulează"}
          </button>

          {!isReadonly && canGenerate && !state.createdFormId ? (
            <button onClick={handleSubmit} className="btn btn-success">
              Generează
            </button>
          ) : null}
        </div>

        {state.errorMsg ? <div className="error">&#9888;&nbsp;{state.errorMsg}</div> : null}

        <input ref={fileRef} type="file" style={{ display: "none" }} />
      </div>
    </section>
  );
}

export default ClientFormFisaServiceDetails;